import type { NodePropertySchema } from '../../../types/node-properties';

/**
 * GraphQL Node Schema
 * 
 * Comprehensive GraphQL implementation with:
 * - Queries and mutations
 * - Variable interpolation
 * - Multiple authentication methods
 * - Custom headers
 * - Pagination support
 * - Introspection capabilities
 */
const schema: NodePropertySchema = {
  nodeType: 'graphql',
  version: '1.0.0',
  
  properties: [
    // ==================================================================
    // CONNECTION
    // ==================================================================
    {
      key: 'url',
      label: 'GraphQL Endpoint URL',
      type: 'string',
      placeholder: 'https://api.example.com/graphql',
      required: true,
      description: 'The GraphQL endpoint URL',
      validation: [
        {
          type: 'required',
          message: 'GraphQL endpoint URL is required'
        },
        {
          type: 'url',
          message: 'Please enter a valid URL'
        }
      ]
    },
    
    // ==================================================================
    // AUTHENTICATION
    // ==================================================================
    {
      key: 'authentication',
      label: 'Authentication',
      type: 'options',
      options: [
        { name: 'None', value: 'none' },
        { name: 'Bearer Token', value: 'bearer' },
        { name: 'API Key', value: 'apiKey' },
        { name: 'Basic Auth', value: 'basicAuth' },
        { name: 'OAuth2', value: 'oauth2' },
        { name: 'Credential', value: 'credential' }
      ],
      default: 'none',
      description: 'Authentication method to use'
    },
    
    // Bearer Token
    {
      key: 'bearerToken',
      label: 'Bearer Token',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          authentication: ['bearer']
        }
      },
      placeholder: 'your-token-here',
      description: 'Bearer token for authentication',
      typeOptions: {
        password: true
      }
    },
    
    // API Key
    {
      key: 'apiKeyHeader',
      label: 'API Key Header Name',
      type: 'string',
      default: 'X-API-Key',
      displayOptions: {
        show: {
          authentication: ['apiKey']
        }
      },
      description: 'Name of the header containing the API key'
    },
    {
      key: 'apiKeyValue',
      label: 'API Key Value',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          authentication: ['apiKey']
        }
      },
      description: 'The API key value',
      typeOptions: {
        password: true
      }
    },
    
    // Basic Auth
    {
      key: 'basicAuthUser',
      label: 'Username',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          authentication: ['basicAuth']
        }
      }
    },
    {
      key: 'basicAuthPassword',
      label: 'Password',
      type: 'string',
      default: '',
      displayOptions: {
        show: {
          authentication: ['basicAuth']
        }
      },
      typeOptions: {
        password: true
      }
    },
    
    // Credential
    {
      key: 'credentialId',
      label: 'Credential',
      type: 'credential',
      default: '',
      displayOptions: {
        show: {
          authentication: ['credential', 'oauth2']
        }
      },
      credentialTypes: [{ name: 'graphqlApi' }]
    },
    
    // ==================================================================
    // OPERATION
    // ==================================================================
    {
      key: 'operation',
      label: 'Operation',
      type: 'options',
      default: 'query',
      options: [
        { name: 'Query', value: 'query' },
        { name: 'Mutation', value: 'mutation' }
      ],
      description: 'Type of GraphQL operation to perform'
    },
    
    {
      key: 'query',
      label: 'GraphQL Query',
      type: 'code',
      required: true,
      placeholder: `query GetUser($id: ID!) {
  user(id: $id) {
    id
    name
    email
  }
}`,
      description: 'GraphQL query or mutation. Supports variable interpolation.',
      typeOptions: {
        language: 'graphql',
        rows: 12
      }
    },
    
    {
      key: 'variables',
      label: 'Variables',
      type: 'code',
      placeholder: `{
  "id": "{{ $node["NodeName"].string }}"
}`,
      description: 'GraphQL variables as JSON. Supports variable interpolation.',
      typeOptions: {
        language: 'json',
        rows: 6
      }
    },
    
    // ==================================================================
    // HEADERS
    // ==================================================================
    {
      key: 'sendHeaders',
      label: 'Custom Headers',
      type: 'boolean',
      default: false,
      description: 'Whether to send custom headers'
    },
    
    {
      key: 'headerParameters',
      label: 'Header Parameters',
      type: 'fixedCollection',
      typeOptions: {
        multipleValues: true
      },
      displayOptions: {
        show: {
          sendHeaders: [true]
        }
      },
      default: {},
      placeholder: 'Add Header',
      fixedCollectionOptions: [
        {
          name: 'parameters',
          displayName: 'Header',
          values: [
            {
              key: 'name',
              label: 'Name',
              type: 'string',
              default: '',
              placeholder: 'X-Custom-Header',
              required: true
            },
            {
              key: 'value',
              label: 'Value',
              type: 'string',
              default: '',
              placeholder: 'header-value'
            }
          ]
        }
      ]
    },
    
    // ==================================================================
    // OPTIONS
    // ==================================================================
    {
      key: 'options',
      label: 'Options',
      type: 'collection',
      placeholder: 'Add Option',
      default: {},
      values: [
        {
          key: 'timeout',
          label: 'Timeout',
          type: 'number',
          default: 30000,
          description: 'Request timeout in milliseconds',
          typeOptions: {
            minValue: 1000,
            maxValue: 300000
          }
        },
        {
          key: 'enableRetry',
          label: 'Enable Retry',
          type: 'boolean',
          default: false,
          description: 'Automatically retry failed requests'
        },
        {
          key: 'maxRetries',
          label: 'Max Retries',
          type: 'number',
          default: 3,
          displayOptions: {
            show: {
              enableRetry: [true]
            }
          },
          typeOptions: {
            minValue: 1,
            maxValue: 10
          }
        },
        {
          key: 'enablePagination',
          label: 'Enable Pagination',
          type: 'boolean',
          default: false,
          description: 'Automatically detect and handle cursor-based pagination'
        },
        {
          key: 'cursorPath',
          label: 'Cursor Path',
          type: 'string',
          default: 'pageInfo.endCursor',
          placeholder: 'pageInfo.endCursor',
          displayOptions: {
            show: {
              enablePagination: [true]
            }
          },
          description: 'JSON path to the cursor value (e.g., "pageInfo.endCursor")'
        },
        {
          key: 'hasNextPagePath',
          label: 'Has Next Page Path',
          type: 'string',
          default: 'pageInfo.hasNextPage',
          placeholder: 'pageInfo.hasNextPage',
          displayOptions: {
            show: {
              enablePagination: [true]
            }
          },
          description: 'JSON path to the hasNextPage boolean'
        },
        {
          key: 'maxPages',
          label: 'Max Pages',
          type: 'number',
          default: 100,
          displayOptions: {
            show: {
              enablePagination: [true]
            }
          },
          typeOptions: {
            minValue: 1,
            maxValue: 1000
          },
          description: 'Maximum number of pages to fetch'
        },
        {
          key: 'cursorVariable',
          label: 'Cursor Variable Name',
          type: 'string',
          default: 'after',
          placeholder: 'after',
          displayOptions: {
            show: {
              enablePagination: [true]
            }
          },
          description: 'Variable name for the cursor in your query (e.g., "after", "cursor")'
        },
        {
          key: 'fullResponse',
          label: 'Include Full Response',
          type: 'boolean',
          default: false,
          description: 'Return full response including headers and status code'
        },
        {
          key: 'allowUnauthorizedCerts',
          label: 'Allow Unauthorized Certificates',
          type: 'boolean',
          default: false,
          description: 'Whether to allow self-signed or invalid SSL certificates'
        }
      ]
    }
  ],
  
  metadata: {
    author: 'Evo E2E Team',
    documentation: 'https://graphql.org/learn/'
  }
};

export default schema;
